import CreatorsDistributionChart from 'Components/charts/CreatorDistributionChart';
import MultiLineChart from 'Components/charts/MultiLineChart';
import React, { useEffect, useMemo, useState } from 'react'
import { Nav, NavItem } from 'reactstrap'
import { CompetitionGroup, CreatorFollowerTier, formattableInsightItem, insightItemsLabel } from 'types/competition-analysis'
import { formatMonthYear, formatNumber, shadeColor, stringToColor } from 'utils/helpers';


const Overview: React.FC<{ competitionGroup: CompetitionGroup, tiers: CreatorFollowerTier[], preview?: boolean }> = ({ competitionGroup, tiers, preview=false }) => {
    const [activeTab, setActiveTab] = useState<"posts" | "engagement_score" | "creators" | "engagements" | "views">('posts');
    const [visibleMap, setVisibleMap] = useState<Record<string, boolean>>({});

    const tiersMap = useMemo(() => {
        return (tiers ?? []).reduce((acc, tier) => {
            acc[tier.slug] = tier;
            return acc;
        }, {} as Record<string, CreatorFollowerTier>)
    }, [tiers])

    useEffect(() => {
        if (!competitionGroup) return;

        const map: Record<string, boolean> = {};
        competitionGroup.accounts.forEach(acc => {
            map[acc.social_account.uuid] = true;
        });
        setVisibleMap(map);
    }, [competitionGroup]);

    const toggleBrand = (uuid: string) => {
        setVisibleMap(prev => ({
            ...prev,
            [uuid]: !prev[uuid]
        }));
    };
    const generateTierShadeMap = (tiers: CreatorFollowerTier[]) => {
        if (!tiers || tiers.length === 0) return {};

        const sorted = [...tiers].sort(
            (a, b) => a.maximum - b.maximum
        );

        const START_SHADE = 90;
        const STEP = 30;

        return sorted.reduce<Record<string, number>>((acc, tier, index) => {
            acc[tier.slug] = START_SHADE - index * STEP;
            return acc;
        }, {});
    };


    const tierShadeMap = useMemo(() => {
        return generateTierShadeMap(tiers);
    }, [tiers]);


    const totalBreakdown = useMemo(() => {
        if (!competitionGroup) return null;
        return competitionGroup.accounts
            .map((a) => (
                {
                    brand: a.social_account.name,
                    color: stringToColor(a.social_account.handle),
                    value: formattableInsightItem.includes(activeTab) ? formatNumber(a.total_insight?.[activeTab] ?? 0) : (a.total_insight?.[activeTab] ?? 0)
                }
            ))
    }, [competitionGroup, activeTab])

    const periodDates = useMemo(() => {
        const dates = new Set<string>();

        for (const account of competitionGroup?.accounts ?? []) {
            for (const insight of account.period_total_insights ?? []) {
                dates.add(insight.date);
            }
        }

        return Array.from(dates).sort(
            (a, b) => new Date(a).getTime() - new Date(b).getTime()
        );
    }, [competitionGroup]);

    const categories = useMemo(() => {
        return periodDates.map(formatMonthYear);
    }, [periodDates]);

    const formattedDateMap = useMemo(() => {
        return Object.fromEntries(
            periodDates.map(date => [formatMonthYear(date), date])
        );
    }, [periodDates]);

    const series = useMemo(() => {
        if (!competitionGroup) return [];

        return competitionGroup.accounts.map(acc => ({
            name: acc.social_account.name,
            color: stringToColor(acc.social_account.handle),
            visible: visibleMap[acc.social_account.uuid] !== false,
            data: acc.period_total_insights.map(i => {
                const v = i.values?.[activeTab as keyof typeof i.values];
                return typeof v === "number" ? v : 0;
            }),
            meta: {
                avatar: acc.social_account.profile_picture_url
            }
        }));
    }, [competitionGroup, activeTab, visibleMap]);

    const tierSeries = useMemo(() => {
        return (
            Object.fromEntries(
                (competitionGroup?.accounts ?? []).map(acc => [
                    acc.social_account.name,
                    Object.fromEntries(
                        (tiers ?? []).map(tier => [
                            tier.slug,
                            acc.period_creator_follower_tier_insights.map(
                                (i: any) => i.values?.[tier.slug]?.creators ?? 0
                            )
                        ])
                    )
                ])
            )
        )
    }, [competitionGroup, tiers])

    const getTiersCategorisations = (seriesName: string, date: string) => {
        return `
        <div style="margin-top: 8px; display: grid; row-gap: 4px;">
        ${tiers.map((tier) => {
            const account = (competitionGroup.accounts ?? []).find((a) => a.social_account?.name === seriesName);
            if (!account) return '';
            const monthData = account.period_creator_follower_tier_insights.find((i) => i.date === formattedDateMap[date])?.values;
            if (!monthData) return "";
            return `
                        <div style="
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            font-size: 12px;
                            color: #374151;
                        ">
                            <div style="display: flex; align-items: center; gap: 6px;">
                               
                                <span>
                                    ${tier.name}
                                </span>
                            </div>
                            <span style="font-weight: 500;">
                                ${formatNumber(monthData[tier.slug].creators ?? 0)}
                            </span>
                        </div>
                    `;
        }).join("")}
        </div>
    `;
    };



    const renderBrandTooltip = ({
        seriesName,
        x,
        y,
        color,
        avatar
    }: {
        seriesName: string;
        x: string;
        y: number;
        color: string;
        avatar?: string;
    }) => {
        console.log('seri', seriesName, x, y)
        return `
            <div style="
                display: flex;
                background: #fff;
                border-radius: 12px;
                padding: 12px 14px;
                box-shadow: 0 10px 25px rgba(0,0,0,0.12);
                min-width: 220px;
                gap: 10px;
                align-items: center;
            ">
                <div style="
                    width: 4px;
                    align-self: stretch;
                    border-radius: 4px;
                    background: ${color};
                "></div>

                <div style="flex: 1">
                    <div style="font-weight: 600; color: #111827;">
                        ${seriesName}
                    </div>
                    <div style="font-size: 12px; color: #6b7280;">
                        ${x}
                    </div>
                    <div style="margin-top: 6px; font-size: 14px; font-weight: 600;">
                        ${(formattableInsightItem.includes(activeTab) ? formatNumber(y) : y)} ${insightItemsLabel[activeTab]}
                    </div>
                    ${activeTab === 'creators' ? getTiersCategorisations(seriesName, x) : ''
            }
                </div>

                ${avatar
                ? `<img src="${avatar}" style="width: 32px; height: 32px; border-radius: 50%; object-fit: cover;" />`
                : ""
            }
            </div>
        `;
    };




    return (
        <div className={`card shadow overflow-hidden ${!preview ? 'm-3 mb-5' : 'preview-overview'}`}>
            <Nav tabs className="d-flex w-100 overview-tabs justify-content-between">
                <NavItem onClick={() => setActiveTab('posts')} className={`overview-tab ${activeTab === 'posts' && 'active'} w-20`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <span className='d-flex align-items-center gap-1 fs-14px'><i className='bx bx-photo-album'></i> Posts</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('engagement_score')} className={`overview-tab ${activeTab === 'engagement_score' && 'active'} w-20`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <span className='d-flex align-items-center gap-1 fs-14px'><i className='bx bx-trending-up'></i> Engagement Score</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('creators')} className={`overview-tab ${activeTab === 'creators' && 'active'} w-20`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <span className='d-flex align-items-center gap-1 fs-14px'> <i className='bx bx-user'></i> Creators</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('engagements')} className={`overview-tab ${activeTab === 'engagements' && 'active'} w-20`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <span className='d-flex align-items-center gap-1 fs-14px'> <i className='bx bx-heart'></i> Engagements</span>
                    </div>
                </NavItem>
                <NavItem onClick={() => setActiveTab('views')} className={`overview-tab ${activeTab === 'views' && 'active'} w-20`}>
                    <div className="d-flex flex-column align-items-center justify-content-center h-100">
                        <span className='d-flex align-items-center gap-1 fs-14px'> <i className='mdi mdi-eye-outline'></i> Views</span>
                    </div>
                </NavItem>
            </Nav>
            <div>
                {
                    totalBreakdown &&
                    <div className='py-4 px-5'>
                        <h6 className='total-breakdown-heading'>Breakdown</h6>
                        <div className='d-flex align-items-center gap-3 flex-wrap'>
                            {
                                competitionGroup.accounts.map((acc) => {
                                    const isActive = visibleMap[acc.social_account.uuid] !== false;
                                    const color = stringToColor(acc.social_account.handle);

                                    const value = formattableInsightItem.includes(activeTab)
                                        ? formatNumber(acc.total_insight?.[activeTab] ?? 0)
                                        : acc.total_insight?.[activeTab] ?? 0;

                                    return (
                                        <span
                                            key={acc.social_account.uuid}
                                            onClick={() => toggleBrand(acc.social_account.uuid)}
                                            className="breakdown-item d-flex align-items-center gap-1"
                                            style={{ cursor: "pointer", opacity: isActive ? 1 : 0.6 }}
                                        >
                                            <span
                                                style={{
                                                    display: "inline-block",
                                                    marginTop: "1px",
                                                    height: "10px",
                                                    width: "10px",
                                                    borderRadius: "50%",
                                                    backgroundColor: isActive ? color : "#d1d5db" // 👈 gray when off
                                                }}
                                            ></span>
                                            <span className="fw-bolder">
                                                {acc.social_account.name}:
                                            </span>
                                            {value}
                                        </span>
                                    );
                                })
                            }
                        </div>
                    </div>
                }
                <div className="py-4 px-5">
                    <MultiLineChart
                        categories={categories}
                        series={series}
                        height={250}
                        tooltipRenderer={renderBrandTooltip}
                    />
                </div>
            </div>

        </div>
    )
}

export default Overview
